import React from 'react';
import { Icon } from '@iconify/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/shadcn/dialog';
import { Badge } from '@/components/shadcn/badge';
import { Button } from '@/components/shadcn/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/shadcn/table';
import { Separator } from '@/components/shadcn/separator';

export interface MabarInvoiceData {
  id: number;
  participant_id: number;
  name: string;
  email: string;
  wa_number?: string;
  role?: string;
  event_title: string;
  event_slug: string;
  order_number: string;
  subtotal?: number;
  payment_fee?: number;
  community_receives?: number;
  total_amount?: number;
  amount: number;
  payment_method: string;
  payment_status: string;
  invoice_url?: string;
  paid_at?: string | null;
  created_at: string;
}

interface MabarInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: MabarInvoiceData | null;
}

const formatCurrency = (val: number) => `Rp. ${val.toLocaleString('id-ID')}`;

const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const MabarInvoiceModal: React.FC<MabarInvoiceModalProps> = ({ isOpen, onClose, transaction }) => {
  if (!transaction) return null;

  const isPaid = transaction.payment_status?.toLowerCase() === 'paid';
  const subtotalVal =
    transaction.subtotal && transaction.subtotal > 0 ? transaction.subtotal : transaction.amount;
  const paymentFeeVal = transaction.payment_fee || 0;
  const communityReceivesVal =
    transaction.community_receives && transaction.community_receives > 0
      ? transaction.community_receives
      : Math.max(0, subtotalVal - paymentFeeVal);

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden bg-white">
        <DialogHeader className="sr-only">
          <DialogTitle>Detail Invoice #{transaction.order_number}</DialogTitle>
        </DialogHeader>

        {/* Header Logo & Order ID */}
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <img src="https://cdn.sporto.id/sporto-black.png" alt="Sporto" className="max-w-40" />
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-800">
                No. Order: #{transaction.order_number}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Tanggal: {formatDate(transaction.created_at)}
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <div className="w-full">
            <div
              className={`text-center py-3 px-6 rounded-lg w-full font-bold text-lg tracking-wide uppercase ${
                isPaid ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'
              }`}
            >
              {isPaid ? 'PAID' : 'UNPAID'}
            </div>
          </div>
        </div>

        <Separator />

        {/* Customer & Event Information */}
        <div className="p-6">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Informasi Peserta & Event</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-gray-700">
                <Icon icon="mdi:account" className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className="font-semibold text-gray-900">{transaction.name}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-700">
                <Icon icon="mdi:whatsapp" className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span>{transaction.wa_number || '-'}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-700">
                <Icon icon="mdi:email-outline" className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className="break-all">{transaction.email || '-'}</span>
              </div>
            </div>

            <div className="space-y-2 md:text-right">
              <div>
                <p className="text-xs text-gray-500">Event Mabar</p>
                <p className="font-bold text-gray-900">{transaction.event_title}</p>
              </div>
              {transaction.role && (
                <div>
                  <p className="text-xs text-gray-500">Posisi / Role</p>
                  <Badge
                    variant="outline"
                    className="bg-blue-50 text-blue-700 border-blue-200 font-semibold text-xs mt-0.5"
                  >
                    {transaction.role}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Items Table using Shadcn Table */}
        <div className="px-6 pb-6">
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <Table>
              <TableHeader className="bg-gray-100">
                <TableRow>
                  <TableHead className="font-semibold text-gray-700">Item</TableHead>
                  <TableHead className="text-center font-semibold text-gray-700">Qty</TableHead>
                  <TableHead className="text-right font-semibold text-gray-700">Harga</TableHead>
                  <TableHead className="text-right font-semibold text-gray-700">Subtotal</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="bg-white">
                  <TableCell className="font-medium text-gray-900">
                    Pendaftaran Mabar - {transaction.event_title}
                    {transaction.role && (
                      <span className="text-xs text-gray-500 block font-normal">
                        Posisi: {transaction.role}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-center text-gray-900">1</TableCell>
                  <TableCell className="text-right text-gray-900">
                    {formatCurrency(subtotalVal)}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-gray-900">
                    {formatCurrency(subtotalVal)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Payment Summary */}
        <div className="px-6 pb-6">
          <Separator className="mb-5" />
          <h3 className="text-base font-semibold text-gray-900 mb-3">Rincian Biaya Transaksi</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Metode Pembayaran</span>
              <span className="font-semibold text-gray-900 uppercase">
                {transaction.payment_method === 'qris' ? 'QRIS' : transaction.payment_method}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Harga Tiket Mabar (Subtotal)</span>
              <span className="text-gray-900 font-medium">{formatCurrency(subtotalVal)}</span>
            </div>
            {paymentFeeVal > 0 && (
              <div className="flex justify-between text-rose-600">
                <span>Biaya Transaksi (Payment Fee)</span>
                <span>- {formatCurrency(paymentFeeVal)}</span>
              </div>
            )}
            <Separator className="my-2.5" />
            <div className="flex justify-between items-center">
              <div>
                <span className="text-sm font-bold text-gray-900 block">
                  Nominal Diterima Komunitas
                </span>
                <span className="text-xs text-gray-500">Net penerimaan disetor ke Wallet</span>
              </div>
              <span className="text-lg font-bold text-emerald-600">
                {formatCurrency(communityReceivesVal)}
              </span>
            </div>
          </div>
        </div>

        {/* Actions Footer using Shadcn Button */}
        <div className="px-6 py-4 flex justify-end gap-3 border-t border-gray-100 bg-gray-50/50">
          <Button type="button" variant="outline" onClick={onClose}>
            Tutup
          </Button>
          <Button
            type="button"
            onClick={handlePrint}
            className="bg-primary-600 hover:bg-primary-700 text-white flex items-center space-x-2 shadow-sm"
          >
            <Icon icon="mdi:printer" className="w-4 h-4" />
            <span>Cetak Invoice</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MabarInvoiceModal;
